import { Box, CircularProgress, Skeleton } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitCard, KitItem, KitList, KitPill, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { KitAvatar, KitBanner, KitFormBar, KitKV, KitPlate, KitTimeline } from '../common/MobileKitExtras';
import StatusChip from '../common/StatusChip';
import { useAppBarAction, useAppBarTitle } from '../layout/AppBarActions';
import dayjs from 'dayjs';
import { fmtDate, formatKm, initials, orderMoment, orderStatusView, vehicleLabel } from './orderMobileUtils';

/**
 * Detalhe da ordem (somente leitura) no shell mobile — `prototipos-mobile/ordens/detalhe.html`.
 * Só apresentação: dados, permissões e ações vêm de `pages/service_order/View.js`.
 */

const WrenchIcon = protoIcon('wrench');
const PackageIcon = protoIcon('package');
const PrintIcon = protoIcon('print');
const MsgIcon = protoIcon('msg');
const DotsIcon = protoIcon('doth');
const ShareIcon = protoIcon('share');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');
const DownloadIcon = protoIcon('download');
const FileIcon = protoIcon('file');
const SendIcon = protoIcon('send');

const PAYMENT_LABEL = {
    'CRÉDITO': 'Crédito',
    'DÉBITO': 'Débito',
    PIXKEY: 'Pix',
    PIXQRCODE: 'Pix QR Code',
    DINHEIRO: 'Dinheiro',
};

function paymentRow(payment) {
    const base = PAYMENT_LABEL[payment.method] || payment.method;
    if (payment.method === 'CRÉDITO' && payment.NumberInstallments > 1) {
        return { label: `${base} ${payment.NumberInstallments}x`, value: `R$ ${payment.ValueInstallment} × ${payment.NumberInstallments}` };
    }
    return { label: base, value: `R$ ${payment.amount}` };
}

// "17/10 · 17h" (prazo de entrega, como no protótipo)
function deadlineLabel(value) {
    const m = orderMoment(value);
    if (!m) return '—';
    return `${m.format('DD/MM')} · ${m.minute() === 0 ? `${m.format('H')}h` : m.format('HH:mm')}`;
}

export default function MobileOrderDetail({
    loading,
    orderNumber,
    status,
    details,
    usesVehicle,
    items,
    serviceTasks,
    loadingItems,
    finance,
    payments,
    checklist,
    checklistEnabled,
    canEdit,
    canDelete,
    canSendWhatsapp,
    whatsappConnected,
    canOperate,
    printing,
    onPrint,
    onOperate,
    onEdit,
    onCopyLink,
    onSendWhatsapp,
    onShareWhatsapp,
    onDelete,
    onOpenQuote,
    onChecklistPdf,
    downloadingChecklistPdf,
    onChecklistWhatsapp,
}) {
    const [actionsOpen, setActionsOpen] = useState(false);
    const view = orderStatusView(status);

    useAppBarTitle(!loading && orderNumber ? `OS #${orderNumber}` : '');
    useAppBarAction({
        actions: [
            { label: 'Imprimir', icon: PrintIcon, onClick: onPrint, disabled: loading || printing },
            { label: 'Ações', icon: DotsIcon, onClick: () => setActionsOpen(true), disabled: loading },
        ],
    });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <Skeleton variant="rounded" height={250} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={190} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={150} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }

    const created = orderMoment(details.order_date);
    const phone = details.customer_phone || '';
    const showPlate = details.vehicle_plate && details.vehicle_plate !== '-' && details.vehicle_type !== 'bicycle';
    const statusKey = String(status || '').trim().toLowerCase();
    const started = ['em execução', 'iniciada', 'finalizada', 'paga', 'faturada'].includes(statusKey);
    const finished = ['finalizada', 'paga', 'faturada'].includes(statusKey);
    const billed = ['paga', 'faturada'].includes(statusKey);
    const cancelled = statusKey === 'cancelada';

    const stamp = (value) => {
        const m = value ? dayjs(value) : null;
        return m && m.isValid() && m.year() >= 1900 ? `${m.format('DD/MM')} · ${m.format('HH:mm')}` : undefined;
    };
    const timeline = [{ title: 'OS criada', sub: created ? `${created.format('DD/MM')} · ${created.format('HH:mm')}` : undefined, state: 'done' }];
    if (cancelled) {
        timeline.push({ title: 'OS cancelada', sub: details.cancel_reason || undefined, state: 'done' });
    } else {
        timeline.push({ title: 'Iniciada', sub: started ? stamp(details.started_at) : '—', state: started ? 'done' : 'off' });
        timeline.push({ title: 'Finalização', sub: finished ? stamp(details.finished_at) : '—', state: finished ? 'done' : 'off' });
        if (billed) timeline.push({ title: 'Faturada', sub: stamp(details.billed_at), state: 'done' });
    }

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <KitPill tone={view.tone}>{view.label}</KitPill>
                    <Box component="span" sx={{ fontSize: 12.5, color: 'text.disabled' }}>{fmtDate(created)}</Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mt: '12px' }}>
                    <KitAvatar text={initials(details.customer_name)} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ fontWeight: 700, lineHeight: 1.4 }}>{details.customer_name || '—'}</Box>
                        {phone && <Box sx={{ fontSize: 12.5, color: 'text.disabled', lineHeight: 1.4 }}>{phone}</Box>}
                    </Box>
                    {phone && (
                        <Box
                            component="button"
                            type="button"
                            aria-label="Chamar no WhatsApp"
                            onClick={onShareWhatsapp}
                            sx={{ width: 40, height: 40, borderRadius: '12px', border: 0, p: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer', backgroundColor: 'success.bg', color: 'success.main' }}
                        >
                            <MsgIcon size={20} />
                        </Box>
                    )}
                </Box>
                <Box sx={{ height: '1px', backgroundColor: 'divider', m: '14px 0' }} />
                {usesVehicle && <KitKV label="Veículo" value={vehicleLabel(details.vehicle_name) || '—'} />}
                {usesVehicle && showPlate && <KitKV label="Placa" value={<KitPlate>{details.vehicle_plate}</KitPlate>} />}
                {usesVehicle && <KitKV label="KM" value={formatKm(details.vehicle_kilometer)} />}
                <KitKV label="Entrega" value={deadlineLabel(details.delivery_date)} />
                {details.technician_name && <KitKV label="Mecânico" value={details.technician_name} />}
            </KitCard>

            {cancelled && details.cancel_reason && (
                <Box sx={{ mt: '12px' }}>
                    <KitBanner tone="danger" title="Motivo do cancelamento">{details.cancel_reason}</KitBanner>
                </Box>
            )}

            {details.budget_id && details.budget_number && (
                <Box sx={{ mt: '12px' }}>
                    <KitButton variant="soft" size="sm" onClick={onOpenQuote}>
                        <FileIcon size={18} /> Gerada a partir do Orçamento #{details.budget_number}
                    </KitButton>
                </Box>
            )}

            {details.defect_reason && (
                <>
                    <SecH title="Motivo / defeito" />
                    <KitCard>{details.defect_reason}</KitCard>
                </>
            )}

            {details.observations && (
                <>
                    <SecH title="Observações" />
                    <KitCard>{details.observations}</KitCard>
                </>
            )}

            <SecH title="Itens" />
            {loadingItems ? (
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px' }} />
            ) : (
                <KitList>
                    {items.length === 0 && <KitItem icon={PackageIcon} tone="gray" title="Nenhum item nesta ordem" sub="Esta ordem não possui itens registrados" />}
                    {/* Serviços primeiro, depois peças (ordem do protótipo). */}
                    {[...items].sort((a, b) => Number(b.type === 'service') - Number(a.type === 'service')).map((item) => {
                        const isService = item.type === 'service';
                        const tasks = isService ? serviceTasks[item.service_id || item.id] : null;
                        const sub = isService
                            ? ['Serviço', Number(item.quantity) > 1 ? `${item.quantity} × R$ ${item.amount_unit}` : null].filter(Boolean).join(' · ')
                            : `${item.quantity} un${item.item_discount_type ? ` · desc. ${item.item_discount_type}` : ''}`;
                        return (
                            <KitItem
                                key={item.id}
                                icon={isService ? WrenchIcon : PackageIcon}
                                title={item.name}
                                sub={sub}
                                end={<Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>R$ {item.amount_total}</Box>}
                            >
                                {Array.isArray(tasks) && tasks.length > 0 && (
                                    <Box sx={{ mt: '4px', fontSize: 11.5, color: 'text.secondary', lineHeight: 1.4 }}>
                                        {tasks.map((task) => <Box key={task.id} component="div">· {task.task_name}</Box>)}
                                    </Box>
                                )}
                            </KitItem>
                        );
                    })}
                </KitList>
            )}

            <KitCard sx={{ mt: '12px' }}>
                <KitKV totals label="Subtotal" value={`R$ ${finance.subtotal}`} />
                <KitKV totals label="Desconto" value={`- R$ ${finance.discount}`} />
                <KitKV totals big label="Total" value={`R$ ${finance.total}`} />
            </KitCard>

            {payments.length > 0 && (
                <>
                    <SecH title="Pagamento" />
                    <KitCard>
                        {payments.map((payment, index) => {
                            const row = paymentRow(payment);
                            return <KitKV key={index} label={row.label} value={row.value} />;
                        })}
                    </KitCard>
                </>
            )}

            {checklistEnabled && checklist && (
                <>
                    <SecH title="Checklist" />
                    <KitCard>
                        <Box sx={{ fontWeight: 700 }}>{checklist.checklist_name}</Box>
                        <Box sx={{ fontSize: 12, color: 'text.disabled', mt: '2px' }}>
                            {checklist.mechanic_name ? `Preenchido por ${checklist.mechanic_name}` : ''}
                            {checklist.completion_date ? `${checklist.mechanic_name ? ' · ' : ''}${new Date(checklist.completion_date).toLocaleDateString('pt-BR')}` : ''}
                        </Box>
                        <Box sx={{ mt: '8px' }}>
                            {(checklist.evaluations || []).map((evaluation, index) => (
                                <Box key={evaluation.evaluation_id || index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', p: '9px 0', borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Box sx={{ fontSize: 13.5, fontWeight: 600 }}>{evaluation.item_name}</Box>
                                        {evaluation.observation && <Box sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>{evaluation.observation}</Box>}
                                    </Box>
                                    <StatusChip domain="checklist" value={evaluation.answer_description} />
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', gap: '8px', mt: '10px' }}>
                            <KitButton variant="soft" size="sm" onClick={onChecklistPdf} disabled={downloadingChecklistPdf}>
                                {downloadingChecklistPdf ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <DownloadIcon size={18} />} Baixar PDF
                            </KitButton>
                            {canSendWhatsapp && (
                                <KitButton variant="soft" size="sm" onClick={onChecklistWhatsapp} disabled={!whatsappConnected}>
                                    <SendIcon size={18} /> WhatsApp
                                </KitButton>
                            )}
                        </Box>
                    </KitCard>
                </>
            )}

            <SecH title="Histórico" />
            <KitCard>
                <KitTimeline items={timeline} />
            </KitCard>

            {canOperate && (
                <KitFormBar>
                    <KitButton variant="primary" onClick={onOperate}><WrenchIcon size={18} /> Operar</KitButton>
                </KitFormBar>
            )}

            <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Ações" actionList>
                <Box>
                    <KitItem
                        icon={ShareIcon}
                        title="Enviar link ao cliente"
                        sub="Acompanhamento público"
                        chevron
                        sx={{ border: 0, borderBottom: 0, p: '12px 4px' }}
                        onClick={() => { setActionsOpen(false); onCopyLink(); }}
                    />
                    {canSendWhatsapp && (
                        <KitItem
                            icon={MsgIcon}
                            title="Enviar por WhatsApp"
                            sub={whatsappConnected ? undefined : 'WhatsApp não conectado'}
                            chevron
                            sx={{ border: 0, borderBottom: 0, p: '12px 4px', ...(whatsappConnected ? {} : { opacity: 0.5 }) }}
                            onClick={() => { if (!whatsappConnected) return; setActionsOpen(false); onSendWhatsapp(); }}
                        />
                    )}
                    {canEdit && (
                        <KitItem
                            icon={EditIcon}
                            title="Editar dados"
                            chevron
                            sx={{ border: 0, borderBottom: 0, p: '12px 4px' }}
                            onClick={() => { setActionsOpen(false); onEdit(); }}
                        />
                    )}
                    {canDelete && (
                        <KitItem
                            icon={TrashIcon}
                            tone="red"
                            title="Excluir ordem"
                            chevron
                            sx={{ border: 0, borderBottom: 0, p: '12px 4px' }}
                            onClick={() => { setActionsOpen(false); onDelete(); }}
                        />
                    )}
                </Box>
            </MobileSheet>
        </Box>
    );
}
